import { useEffect, useState } from 'react';
import { AlertCircle, Loader2, Plus, Save, Trash2 } from 'lucide-react';
import type {
  FenetreIntervention,
  PeriodeInterdiction,
  UpsertServiceParametresMaintenanceInput,
} from '@mygmao/shared';
import { api, extractErrorMessage } from '../../lib/api';
import { usePermissions } from '../../hooks/usePermissions';

/**
 * Panel paramètres maintenance d'un service (US-SVC-05) :
 *  - délais critique / urgence / prioritaire / normal (en heures)
 *  - fenêtres d'intervention (jour × créneau)
 *  - périodes interdites
 *  - jours interdits
 *  - préavis minimum, durée max
 *  - contraintes d'accès
 */

interface ParametresState {
  delai_critique: number;
  delai_urgence: number;
  delai_prioritaire: number;
  delai_normal: number;
  fenetres_intervention: FenetreIntervention[];
  periodes_interdiction: PeriodeInterdiction[];
  jours_interdits: number[];
  preavis_minimum: number;
  duree_maximale_intervention: number | null;
  contraintes_acces: {
    accompagnement_obligatoire: boolean;
    badge_securise_requis: boolean;
    formation_specifique: string[];
    autre: string;
  };
}

const JOURS_LABELS = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];

function defaultState(): ParametresState {
  return {
    delai_critique: 1,
    delai_urgence: 8,
    delai_prioritaire: 24,
    delai_normal: 72,
    fenetres_intervention: [],
    periodes_interdiction: [],
    jours_interdits: [],
    preavis_minimum: 0,
    duree_maximale_intervention: null,
    contraintes_acces: {
      accompagnement_obligatoire: false,
      badge_securise_requis: false,
      formation_specifique: [],
      autre: '',
    },
  };
}

interface RawParametres {
  delai_critique: number;
  delai_urgence: number;
  delai_prioritaire: number;
  delai_normal: number;
  fenetres_intervention: FenetreIntervention[] | null;
  periodes_interdiction: PeriodeInterdiction[] | null;
  jours_interdits: number[] | null;
  preavis_minimum: number;
  duree_maximale_intervention: number | null;
  contraintes_acces: Partial<ParametresState['contraintes_acces']> | null;
}

function fromRaw(raw: RawParametres): ParametresState {
  return {
    delai_critique: raw.delai_critique,
    delai_urgence: raw.delai_urgence,
    delai_prioritaire: raw.delai_prioritaire,
    delai_normal: raw.delai_normal,
    fenetres_intervention: raw.fenetres_intervention ?? [],
    periodes_interdiction: raw.periodes_interdiction ?? [],
    jours_interdits: raw.jours_interdits ?? [],
    preavis_minimum: raw.preavis_minimum,
    duree_maximale_intervention: raw.duree_maximale_intervention,
    contraintes_acces: {
      accompagnement_obligatoire: raw.contraintes_acces?.accompagnement_obligatoire ?? false,
      badge_securise_requis: raw.contraintes_acces?.badge_securise_requis ?? false,
      formation_specifique: raw.contraintes_acces?.formation_specifique ?? [],
      autre: raw.contraintes_acces?.autre ?? '',
    },
  };
}

export function ParametresMaintenancePanel({ serviceId }: { serviceId: string }) {
  const { can } = usePermissions();
  const canEdit = can('services', 'update');

  const [state, setState] = useState<ParametresState>(defaultState());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get<{ data: RawParametres | null }>(
          `/services/${serviceId}/parametres-maintenance`,
        );
        if (res.data.data) {
          setState(fromRaw(res.data.data));
        }
      } catch (err) {
        setErrorMessage(extractErrorMessage(err));
      } finally {
        setLoading(false);
      }
    })();
  }, [serviceId]);

  function update<K extends keyof ParametresState>(key: K, value: ParametresState[K]) {
    setState((prev) => ({ ...prev, [key]: value }));
  }

  function updateContraintes<K extends keyof ParametresState['contraintes_acces']>(
    key: K,
    value: ParametresState['contraintes_acces'][K],
  ) {
    setState((prev) => ({
      ...prev,
      contraintes_acces: { ...prev.contraintes_acces, [key]: value },
    }));
  }

  function toggleJourInterdit(jour: number) {
    setState((prev) => {
      const next = new Set(prev.jours_interdits);
      if (next.has(jour)) next.delete(jour);
      else next.add(jour);
      return { ...prev, jours_interdits: Array.from(next).sort() };
    });
  }

  function addFenetre() {
    setState((prev) => ({
      ...prev,
      fenetres_intervention: [
        ...prev.fenetres_intervention,
        { jour: 1, debut: '08:00', fin: '18:00' },
      ],
    }));
  }
  function updateFenetre(idx: number, patch: Partial<FenetreIntervention>) {
    setState((prev) => ({
      ...prev,
      fenetres_intervention: prev.fenetres_intervention.map((f, i) =>
        i === idx ? { ...f, ...patch } : f,
      ),
    }));
  }
  function removeFenetre(idx: number) {
    setState((prev) => ({
      ...prev,
      fenetres_intervention: prev.fenetres_intervention.filter((_, i) => i !== idx),
    }));
  }

  function addPeriode() {
    const today = new Date().toISOString().slice(0, 10);
    setState((prev) => ({
      ...prev,
      periodes_interdiction: [
        ...prev.periodes_interdiction,
        { date_debut: today, date_fin: today, motif: '' },
      ],
    }));
  }
  function updatePeriode(idx: number, patch: Partial<PeriodeInterdiction>) {
    setState((prev) => ({
      ...prev,
      periodes_interdiction: prev.periodes_interdiction.map((p, i) =>
        i === idx ? { ...p, ...patch } : p,
      ),
    }));
  }
  function removePeriode(idx: number) {
    setState((prev) => ({
      ...prev,
      periodes_interdiction: prev.periodes_interdiction.filter((_, i) => i !== idx),
    }));
  }

  function addFormation() {
    updateContraintes('formation_specifique', [
      ...state.contraintes_acces.formation_specifique,
      '',
    ]);
  }
  function updateFormation(idx: number, value: string) {
    updateContraintes(
      'formation_specifique',
      state.contraintes_acces.formation_specifique.map((f, i) => (i === idx ? value : f)),
    );
  }
  function removeFormation(idx: number) {
    updateContraintes(
      'formation_specifique',
      state.contraintes_acces.formation_specifique.filter((_, i) => i !== idx),
    );
  }

  async function handleSave() {
    setSaving(true);
    setErrorMessage(null);
    setSuccessMessage(null);
    try {
      const payload: UpsertServiceParametresMaintenanceInput = {
        delai_critique: state.delai_critique,
        delai_urgence: state.delai_urgence,
        delai_prioritaire: state.delai_prioritaire,
        delai_normal: state.delai_normal,
        fenetres_intervention:
          state.fenetres_intervention.length > 0 ? state.fenetres_intervention : null,
        periodes_interdiction:
          state.periodes_interdiction.length > 0 ? state.periodes_interdiction : null,
        jours_interdits: state.jours_interdits,
        preavis_minimum: state.preavis_minimum,
        duree_maximale_intervention: state.duree_maximale_intervention,
        contraintes_acces: {
          accompagnement_obligatoire: state.contraintes_acces.accompagnement_obligatoire,
          badge_securise_requis: state.contraintes_acces.badge_securise_requis,
          formation_specifique: state.contraintes_acces.formation_specifique.filter(
            (f) => f.trim().length > 0,
          ),
          autre: state.contraintes_acces.autre || undefined,
        },
      };
      await api.put(`/services/${serviceId}/parametres-maintenance`, payload);
      setSuccessMessage('Paramètres enregistrés.');
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-6">
        <Loader2 className="w-5 h-5 animate-spin text-primary-500" />
      </div>
    );
  }

  const readonly = !canEdit;

  return (
    <div className="space-y-4">
      {errorMessage && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{errorMessage}</p>
        </div>
      )}
      {successMessage && (
        <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-700">
          {successMessage}
        </div>
      )}

      <Section title="Délais d'intervention (en heures)">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <DelaiField
            label="Critique"
            value={state.delai_critique}
            onChange={(v) => update('delai_critique', v)}
            disabled={readonly}
          />
          <DelaiField
            label="Urgence"
            value={state.delai_urgence}
            onChange={(v) => update('delai_urgence', v)}
            disabled={readonly}
          />
          <DelaiField
            label="Prioritaire"
            value={state.delai_prioritaire}
            onChange={(v) => update('delai_prioritaire', v)}
            disabled={readonly}
          />
          <DelaiField
            label="Normal"
            value={state.delai_normal}
            onChange={(v) => update('delai_normal', v)}
            disabled={readonly}
          />
        </div>
      </Section>

      <Section
        title="Fenêtres d'intervention"
        action={
          !readonly && (
            <button
              onClick={addFenetre}
              className="text-xs text-primary-600 hover:text-primary-700 inline-flex items-center"
            >
              <Plus className="w-3 h-3 mr-1" />
              Ajouter
            </button>
          )
        }
      >
        {state.fenetres_intervention.length === 0 ? (
          <p className="text-xs text-gray-500 italic">Aucune fenêtre configurée.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs text-gray-500 uppercase">
              <tr>
                <th className="text-left font-medium pb-1">Jour</th>
                <th className="text-left font-medium pb-1">Début</th>
                <th className="text-left font-medium pb-1">Fin</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {state.fenetres_intervention.map((f, idx) => (
                <tr key={idx} className="border-t border-gray-100">
                  <td className="py-1.5 pr-2">
                    <select
                      value={f.jour}
                      onChange={(e) => updateFenetre(idx, { jour: Number(e.target.value) })}
                      disabled={readonly}
                      className={smallInputClass}
                    >
                      {JOURS_LABELS.map((label, i) => (
                        <option key={i} value={i}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="py-1.5 pr-2">
                    <input
                      type="time"
                      value={f.debut}
                      onChange={(e) => updateFenetre(idx, { debut: e.target.value })}
                      disabled={readonly}
                      className={smallInputClass}
                    />
                  </td>
                  <td className="py-1.5 pr-2">
                    <input
                      type="time"
                      value={f.fin}
                      onChange={(e) => updateFenetre(idx, { fin: e.target.value })}
                      disabled={readonly}
                      className={smallInputClass}
                    />
                  </td>
                  <td className="text-right">
                    {!readonly && (
                      <button
                        onClick={() => removeFenetre(idx)}
                        className="p-1.5 rounded text-red-600 hover:bg-red-50"
                        aria-label="Supprimer"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      <Section
        title="Périodes interdites"
        action={
          !readonly && (
            <button
              onClick={addPeriode}
              className="text-xs text-primary-600 hover:text-primary-700 inline-flex items-center"
            >
              <Plus className="w-3 h-3 mr-1" />
              Ajouter
            </button>
          )
        }
      >
        {state.periodes_interdiction.length === 0 ? (
          <p className="text-xs text-gray-500 italic">Aucune période bloquée.</p>
        ) : (
          <ul className="space-y-2">
            {state.periodes_interdiction.map((p, idx) => (
              <li key={idx} className="flex items-center gap-2">
                <input
                  type="date"
                  value={p.date_debut}
                  onChange={(e) => updatePeriode(idx, { date_debut: e.target.value })}
                  disabled={readonly}
                  className={smallInputClass}
                />
                <span className="text-xs text-gray-400">→</span>
                <input
                  type="date"
                  value={p.date_fin}
                  onChange={(e) => updatePeriode(idx, { date_fin: e.target.value })}
                  disabled={readonly}
                  className={smallInputClass}
                />
                <input
                  type="text"
                  placeholder="Motif"
                  value={p.motif ?? ''}
                  onChange={(e) => updatePeriode(idx, { motif: e.target.value })}
                  disabled={readonly}
                  className={smallInputClass + ' flex-1'}
                />
                {!readonly && (
                  <button
                    onClick={() => removePeriode(idx)}
                    className="p-1.5 rounded text-red-600 hover:bg-red-50"
                    aria-label="Supprimer"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title="Jours interdits">
        <div className="flex flex-wrap gap-2">
          {JOURS_LABELS.map((label, i) => {
            const active = state.jours_interdits.includes(i);
            return (
              <button
                key={i}
                type="button"
                onClick={() => toggleJourInterdit(i)}
                disabled={readonly}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                  active
                    ? 'bg-red-100 text-red-800 border-red-200'
                    : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                } ${readonly ? 'cursor-not-allowed opacity-60' : ''}`}
              >
                {label}
              </button>
            );
          })}
        </div>
      </Section>

      <Section title="Autres contraintes">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Préavis minimum (h)
            </label>
            <input
              type="number"
              min="0"
              value={state.preavis_minimum}
              onChange={(e) => update('preavis_minimum', Number(e.target.value))}
              disabled={readonly}
              className={smallInputClass}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Durée max intervention (h)
            </label>
            <input
              type="number"
              min="0"
              value={state.duree_maximale_intervention ?? ''}
              onChange={(e) =>
                update(
                  'duree_maximale_intervention',
                  e.target.value === '' ? null : Number(e.target.value),
                )
              }
              disabled={readonly}
              className={smallInputClass}
            />
          </div>
        </div>
      </Section>

      <Section title="Contraintes d'accès">
        <div className="space-y-2">
          <label className="flex items-center space-x-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={state.contraintes_acces.accompagnement_obligatoire}
              onChange={(e) => updateContraintes('accompagnement_obligatoire', e.target.checked)}
              disabled={readonly}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span>Accompagnement obligatoire</span>
          </label>
          <label className="flex items-center space-x-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={state.contraintes_acces.badge_securise_requis}
              onChange={(e) => updateContraintes('badge_securise_requis', e.target.checked)}
              disabled={readonly}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span>Badge sécurisé requis</span>
          </label>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-medium text-gray-700">
                Formations spécifiques
              </label>
              {!readonly && (
                <button
                  onClick={addFormation}
                  className="text-xs text-primary-600 hover:text-primary-700 inline-flex items-center"
                >
                  <Plus className="w-3 h-3 mr-1" />
                  Ajouter
                </button>
              )}
            </div>
            {state.contraintes_acces.formation_specifique.length === 0 ? (
              <p className="text-xs text-gray-500 italic">Aucune formation requise.</p>
            ) : (
              <ul className="space-y-1.5">
                {state.contraintes_acces.formation_specifique.map((f, idx) => (
                  <li key={idx} className="flex items-center gap-2">
                    <input
                      type="text"
                      value={f}
                      onChange={(e) => updateFormation(idx, e.target.value)}
                      disabled={readonly}
                      className={smallInputClass + ' flex-1'}
                      placeholder="Habilitation électrique, etc."
                    />
                    {!readonly && (
                      <button
                        onClick={() => removeFormation(idx)}
                        className="p-1.5 rounded text-red-600 hover:bg-red-50"
                        aria-label="Supprimer"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Autres contraintes
            </label>
            <textarea
              value={state.contraintes_acces.autre}
              onChange={(e) => updateContraintes('autre', e.target.value)}
              disabled={readonly}
              rows={2}
              className={smallInputClass}
            />
          </div>
        </div>
      </Section>

      {!readonly && (
        <div className="flex justify-end pt-2">
          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center px-4 py-2 rounded-lg shadow-sm text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 disabled:opacity-50"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Enregistrer
          </button>
        </div>
      )}
    </div>
  );
}

function Section({
  title,
  action,
  children,
}: {
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-lg border border-gray-200 p-3 bg-white">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{title}</h4>
        {action}
      </div>
      {children}
    </section>
  );
}

function DelaiField({
  label,
  value,
  onChange,
  disabled,
}: {
  label: string;
  value: number;
  onChange(v: number): void;
  disabled?: boolean;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <input
        type="number"
        min="0"
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        disabled={disabled}
        className={smallInputClass}
      />
    </div>
  );
}

const smallInputClass =
  'w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-50 disabled:text-gray-500';
